import produce from "immer";
import create, { StoreApi } from "zustand";
import { createContext, useContext, useMemo, useCallback } from "react";
import {
  blankDimension,
  blankMetricGroup,
  defaultMetricGroup,
  defaultQueryGroup,
  Dimension,
  DimensionType,
  ExplorerError,
  ExplorerQueryContext,
  ExplorerSource,
  FilterResponse,
  Metric,
  MetricGroup,
  RecodedValue,
  ResultLayout,
  SavedQuery,
  SavedQueryForm,
  SeamsCrossed,
  sortQuery,
  Value,
} from "../models/Explorer";
import { TableData } from "../models/TableData";
import { ExplorerAPI, scopeForEnvironment } from "../util/Requests";
import {
  currentSeasonOrGuess,
  knownCurrentSeason,
  loadCurrentSeason,
} from "../util/season";
import {
  CacheKey,
  readFromCache,
  writeToCache,
  ExplorerEnvironment,
} from "./Cache";
import { Auth } from "aws-amplify";
import { useLeagueEnvironment } from "./LeagueEnvironmentProvider";

export { ExplorerEnvironment };

export enum QueryStatus {
  None = 0,
  Requesting = 1,
  Complete = 2,
  Failed = 3,
}

export interface PlayerFilterOptionsData {
  values: Value[];
  loading: boolean;
  error?: string;
  scope: { seasons: string; leagues: string };
}

export interface ExplorerState {
  filters: FilterResponse | null;
  metrics: string[];
  metricGroups: MetricGroup[];
  data: TableData;
  savedQueries: SavedQuery[][];
  queryStatus: QueryStatus;
  explorerStatus: QueryStatus;
  savedQueriesStatus: QueryStatus;
  savedQuery?: SavedQuery;
  pendingController?: AbortController;
  error?: ExplorerError;
  environment: ExplorerEnvironment;
  api: ExplorerAPI;
  playerFilterOptions: PlayerFilterOptionsData | null;
  playerFilterOptionsController?: AbortController;
  /**
   * exec_query's `queryContext`: global filters plus scope / source / resolution.
   * Lazy cells merge its filters and forward its source.
   */
  queryContext?: ExplorerQueryContext;
  /** Saved values the engine recoded while binding this result (exec_query `recoded`) */
  recoded: RecodedValue[];
  /** Items whose definition changes across the sources this result spans */
  seamsCrossed: SeamsCrossed;
  /** The source this result's query is pinned to (exec_query `pinnedSource`), else null */
  pinnedSource: ExplorerSource | null;
  /** exec_query `layout`: dimension columns (primary or not) and whose side the rows are, for the grid's chips */
  layout: ResultLayout | null;
  /** Table state to apply when data arrives (from URL or saved query) */
  pendingTableState?: string;
}

async function groupSavedQueries(
  queries: SavedQuery[],
): Promise<SavedQuery[][]> {
  const token = (await Auth.currentSession()).getIdToken();
  const userId = token.payload.sub;
  const groupedQueries: SavedQuery[][] = [];
  const queriesByUserId: { [key: string]: SavedQuery[] } = {};

  queries.forEach((query: SavedQuery) => {
    if (!queriesByUserId[query.USER_ID]) {
      queriesByUserId[query.USER_ID] = [];
    }
    query = Object.assign(query, { IS_OWNER: query.USER_ID === userId });
    queriesByUserId[query.USER_ID].push(query);
  });

  for (const userId in queriesByUserId) {
    groupedQueries.push(queriesByUserId[userId]);
  }

  groupedQueries.sort((a, b) => {
    if (a[0].USER_ID === userId || b[0].USER_ID === "no owner") {
      return -1;
    }
    if (a[0].USER_ID === "no owner" || b[0].USER_ID === userId) {
      return 1;
    } else return a[0].USER_ID.localeCompare(b[0].USER_ID);
  });

  return groupedQueries;
}

export interface ExplorerActions {
  updateMetricGroup: (
    metricGroupIndex: number,
    metricGroup: MetricGroup,
  ) => void;
  updateDimension: (
    metricGroupIndex: number,
    dimensionType: DimensionType,
    dimensionIndex: number,
    dimension: Dimension,
  ) => void;
  execQuery: (accessType: string, queryId?: string) => void;
  initExplorer: (hash?: string) => void;
  addMetricGroup: () => void;
  removeMetricGroup: (metricGroupIndex: number) => void;
  addDimension: (
    metricGroupIndex: number,
    dimensionType: DimensionType,
  ) => void;
  removeDimension: (
    metricGroupIndex: number,
    dimensionType: DimensionType,
    dimensionIndex: number,
  ) => void;
  loadSavedQuery: (id: string) => void;
  saveQuery: (queryForm: SavedQueryForm) => Promise<any>;
  editQuery: (queryId: string, queryForm: SavedQueryForm) => void;
  archiveQuery: (queryId: string) => void;
  claimQuery: (queryId: string) => void;
  clearError: () => void;
  fetchPlayerFilterOptions: () => Promise<void>;
  setDimensionRatio: (
    metricGroupIndex: number,
    ratio: Metric | null,
  ) => void;
  /** Dev source picker: force one provider (element 0 `source`), or null for season defaults */
  setQuerySource: (source: ExplorerSource | null) => void;
}

export type ExplorerStore = StoreApi<ExplorerState & ExplorerActions>;

/**
 * Default groups built before /api/current_season answered (the first visit
 * this session), so their season is a guess. initExplorer swaps in the answer
 * before the default query runs, unless the user has edited the group since.
 */
const guessedDefaults = new WeakSet<MetricGroup>();

function initialQueryGroup(environment: ExplorerEnvironment): MetricGroup {
  const scope = scopeForEnvironment(environment);
  const group = defaultQueryGroup(scope, currentSeasonOrGuess(scope));
  if (!knownCurrentSeason(scope)) guessedDefaults.add(group);
  return group;
}

const createExplorerStore = (environment: ExplorerEnvironment) =>
  create<ExplorerState & ExplorerActions>((set, get) => ({
    /* STATE */
    filters: null,
    metrics: [],
    metricGroups: [initialQueryGroup(environment), defaultMetricGroup],
    data: [],
    savedQueries: [],
    queryStatus: QueryStatus.None,
    explorerStatus: QueryStatus.None,
    savedQueriesStatus: QueryStatus.None,
    queryName: "",
    environment,
    api: new ExplorerAPI(environment),
    playerFilterOptions: null,
    recoded: [],
    seamsCrossed: {},
    pinnedSource: null,
    layout: null,

    /* ACTIONS */
    updateMetricGroup: (metricGroupIndex: number, metricGroup: MetricGroup) => {
      set(
        produce((draft) => {
          draft.metricGroups.splice(metricGroupIndex, 1, metricGroup);
        }),
      );
    },

    addMetricGroup: () => {
      set(
        produce((draft) => {
          draft.metricGroups.push(blankMetricGroup);
        }),
      );
    },

    removeMetricGroup: (metricGroupIndex: number) => {
      set(
        produce((draft) => {
          if (metricGroupIndex > 0 && draft.metricGroups.length > 2) {
            draft.metricGroups.splice(metricGroupIndex, 1);
          }
        }),
      );
    },

    execQuery: (accessType, queryId) => {
      const { metricGroups: query, pendingController, api } = get();
      if (pendingController) {
        pendingController.abort();
        // Reset status when aborting previous request
        set({ queryStatus: QueryStatus.None });
      }
      const controller = new AbortController();
      set({
        queryStatus: QueryStatus.Requesting,
        pendingController: controller,
        error: undefined,
      });
      const sortedQuery = sortQuery(query);
      api
        .executeQuery(sortedQuery, accessType, queryId)
        .then((response) => {
          if (controller.signal.aborted) {
            set({ queryStatus: QueryStatus.None });
            return;
          }
          if (!response.data.data && !Array.isArray(response.data)) {
            throw new Error(
              "Query results too large for the browser to process. Try narrowing your filters or removing large detail columns (e.g. ActionDetailChart).",
            );
          }
          let data = response.data.data
            ? JSON.parse(response.data.data)
            : response.data;
          const savedQuery = response.data.saved_query
            ? JSON.parse(response.data.saved_query)
            : undefined;
          set({
            data: data,
            queryStatus: QueryStatus.Complete,
            savedQuery: savedQuery,
            queryContext: response.data.queryContext,
            recoded: Array.isArray(response.data.recoded)
              ? response.data.recoded
              : [],
            seamsCrossed:
              response.data.seams_crossed &&
              typeof response.data.seams_crossed === "object"
                ? response.data.seams_crossed
                : {},
            pinnedSource: response.data.pinnedSource ?? null,
            layout: response.data.layout ?? null,
            pendingController: undefined,
          });
        })
        .catch((e) => {
          if (controller.signal.aborted) {
            set({ queryStatus: QueryStatus.None });
            return;
          }
          console.log("error executing query: ", e);

          // Extract structured error from backend response (e.g. HTTP 422)
          const backendError = e.response?.data;
          const message =
            backendError?.error || backendError?.message || e.message;
          const errorType = backendError?.errorType;
          const { error: _e, message: _m, errorType: _t, ...details } =
            backendError && typeof backendError === "object" ? backendError : ({} as any);

          set(
            produce((draft) => {
              draft.queryStatus = QueryStatus.Failed;
              draft.error = { message, errorType, details };
              draft.data = [];
              draft.queryContext = undefined;
              draft.recoded = [];
              draft.seamsCrossed = {};
              draft.pinnedSource = null;
              draft.layout = null;
              draft.pendingController = undefined;
            }),
          );
        });
    },

    initExplorer: async (hash) => {
      const { environment, api } = get();
      set({
        explorerStatus: QueryStatus.Requesting,
        savedQueriesStatus: QueryStatus.Requesting,
      });
      readFromCache(CacheKey.Filters, environment).then((cachedFilters) => {
        if (cachedFilters) {
          set({ filters: cachedFilters as FilterResponse });
        }
      });
      readFromCache(CacheKey.Metrics, environment).then((cachedMetrics) => {
        if (cachedMetrics) {
          set({ metrics: cachedMetrics as string[] });
        }
      });

      Promise.all([api.getFilters(), api.getMetrics(), api.loadQueries()])
        .then(
          async ([
            { data: filters },
            { data: metrics },
            { data: savedQueries },
          ]) => {
            writeToCache(CacheKey.Filters, filters, environment);
            writeToCache(CacheKey.Metrics, metrics, environment);
            const groupedQueries = await groupSavedQueries(savedQueries);
            console.log("grouped queries: ", groupedQueries);
            set({
              metrics,
              filters,
              savedQueries: groupedQueries,
              explorerStatus: QueryStatus.Complete,
              savedQueriesStatus: QueryStatus.Complete,
            });
            // Pre-fetch player options based on default Season filter
            get().fetchPlayerFilterOptions();
          },
        )
        .catch(() => {
          set({ explorerStatus: QueryStatus.Failed });
        });

      if (hash) {
        api
          .loadQuery(hash)
          .then(async ({ data }) => {
            set({ metricGroups: sortQuery(JSON.parse(data.QUERY)) });
            get().execQuery("direct_link", hash);
          })
          .catch((e) => {
            const msg = e.response?.data?.message || e.message;
            console.log("load query failed with error: ", msg);
            set(
              produce((draft) => {
                draft.queryStatus = QueryStatus.Failed;
                draft.error = { message: `Error loading query: ${msg}` };
                draft.data = [];
              }),
            );
          });
      } else {
        loadCurrentSeason(api).then(({ season }) => {
          if (guessedDefaults.has(get().metricGroups[0])) {
            set(
              produce((draft) => {
                draft.metricGroups[0] = defaultQueryGroup(api.scope, season);
              }),
            );
            get().fetchPlayerFilterOptions();
          }
          get().execQuery("default");
        });
      }
    },

    updateDimension: (
      metricGroupIndex: number,
      dimensionType: DimensionType,
      dimensionIndex: number,
      dimension: Dimension,
    ) => {
      // Check if this is a Season or League filter being updated
      const isSeasonOrLeague =
        dimension.name?.value === "Season" ||
        dimension.name?.value === "League";

      set(
        produce((draft) => {
          draft.metricGroups[metricGroupIndex][dimensionType][dimensionIndex] =
            dimension;
        }),
      );

      // Proactively fetch new player options when scope changes
      if (isSeasonOrLeague && dimensionType === "filters") {
        get().fetchPlayerFilterOptions();
      }
    },

    addDimension: (metricGroupIndex: number, dimensionType: DimensionType) => {
      set(
        produce((draft) => {
          draft.metricGroups[metricGroupIndex][dimensionType].push(
            blankDimension,
          );
        }),
      );
    },

    removeDimension: (
      metricGroupIndex: number,
      dimensionType: DimensionType,
      dimensionIndex: number,
    ) => {
      set(
        produce((draft) => {
          draft.metricGroups[metricGroupIndex][dimensionType].splice(
            dimensionIndex,
            1,
          );
        }),
      );
    },

    loadSavedQuery: async (id: string) => {
      const { api } = get();
      set({
        explorerStatus: QueryStatus.Requesting,
        savedQueriesStatus: QueryStatus.Requesting,
      });
      const savedQuery = (await api.loadQuery(id)).data;
      // Clear data BEFORE setting new state — this ensures the grid sees
      // an empty→populated transition, which is what triggers table state
      // application. Without this, the read effect fires against stale data.
      set({
        data: [],
        metricGroups: sortQuery(JSON.parse(savedQuery.QUERY)),
        savedQuery: savedQuery,
        savedQueriesStatus: QueryStatus.Complete,
        pendingTableState: savedQuery.TABLE_STATE || undefined,
      });
      get().execQuery("saved_query", id);
    },

    saveQuery: async (queryForm: SavedQueryForm) => {
      const { api } = get();
      return new Promise<void>((resolve, reject) => {
        const q = get().metricGroups;
        const sortedQuery = sortQuery(q);
        // Capture current table state from URL
        const currentTableState =
          new URLSearchParams(window.location.search).get("ts") || undefined;
        const query = {
          name: queryForm.queryName,
          description: queryForm.description,
          saveType: queryForm.saveType,
          query: sortedQuery,
          tableState: currentTableState,
          tags: queryForm.tags?.length ? queryForm.tags : undefined,
        };

        const sq = {
          NAME: query.name,
          DESCRIPTION: query.description,
          QUERY: JSON.stringify(query.query),
          TABLE_STATE: currentTableState,
          USER_ID: "",
          USER_NAME: "",
          IS_OWNER: true,
          LAST_SAVED: "",
          SAVE_TYPE: query.saveType,
        };
        set({ savedQueriesStatus: QueryStatus.Requesting });

        api
          .saveQuery(query)
          .then((response) => {
            api.loadQueries().then(async ({ data }) => {
              const groupedQueries = await groupSavedQueries(data);
              set({
                savedQueries: groupedQueries,
                savedQueriesStatus: QueryStatus.Complete,
                savedQuery: Object.assign(sq, { ID: response.data.query_id }),
              });
              resolve();
            });
          })
          .catch((err) => {
            console.log("error saving query: ", err);
            set({
              error: {
                message: `Error saving query: ${
                  err.response?.data.message || err.message
                }`,
              },
              savedQueriesStatus: QueryStatus.Failed,
            });
            reject(err.response?.data.message || err.message);
          });
      });
    },

    editQuery: async (queryId: string, queryForm: SavedQueryForm) => {
      const { api } = get();
      const query = {
        name: queryForm.queryName,
        description: queryForm.description,
        saveType: queryForm.saveType,
        tags: queryForm.tags?.length ? queryForm.tags : undefined,
      };

      set({ savedQueriesStatus: QueryStatus.Requesting });
      api
        .editQuery(queryId, query)
        .then((response) => {
          api.loadQueries().then(async ({ data }) => {
            const groupedQueries = await groupSavedQueries(data);
            const sq = await get().savedQuery;
            if (sq && sq.ID === queryId) {
              sq.NAME = queryForm.queryName;
              sq.DESCRIPTION = queryForm.description;
              sq.SAVE_TYPE = queryForm.saveType;
            }
            set({
              savedQueries: groupedQueries,
              savedQueriesStatus: QueryStatus.Complete,
              savedQuery: sq,
            });
          });
        })
        .catch((err) => {
          console.log("error editing query: ", err);
          set({
            error: {
              message: `Error editing query: ${
                err.response?.data.message || err.message
              }`,
            },
            savedQueriesStatus: QueryStatus.Failed,
          });
        });
    },

    archiveQuery: async (queryId: string) => {
      const { api } = get();
      set({ savedQueriesStatus: QueryStatus.Requesting });
      api
        .archiveQuery(queryId)
        .then((response) => {
          api.loadQueries().then(async ({ data }) => {
            const groupedQueries = await groupSavedQueries(data);
            const sq = await get().savedQuery;
            if (sq && sq.ID === queryId) {
              sq.NAME = "";
              sq.SAVE_TYPE = "archived";
            }
            set({
              savedQueries: groupedQueries,
              savedQueriesStatus: QueryStatus.Complete,
              savedQuery: sq,
            });
          });
        })
        .catch((err) => {
          console.log("error archiving query: ", err);
          set({
            error: {
              message: `Error archiving query: ${
                err.response?.data.message || err.message
              }`,
            },
            savedQueriesStatus: QueryStatus.Failed,
          });
        });
    },

    claimQuery: async (queryId: string) => {
      const { api } = get();
      set({ savedQueriesStatus: QueryStatus.Requesting });
      api
        .claimQuery(queryId)
        .then((response) => {
          api.loadQueries().then(async ({ data }) => {
            const groupedQueries = await groupSavedQueries(data);
            set({
              savedQueries: groupedQueries,
              savedQueriesStatus: QueryStatus.Complete,
            });
          });
        })
        .catch((err) => {
          console.log("error claiming query: ", err);
          set({
            error: {
              message: `Error claiming query: ${
                err.response?.data.message || err.message
              }`,
            },
            savedQueriesStatus: QueryStatus.Failed,
          });
        });
    },

    setDimensionRatio: (metricGroupIndex: number, ratio: Metric | null) => {
      set(
        produce((draft) => {
          draft.metricGroups[metricGroupIndex].dimensionRatio = ratio;
        }),
      );
    },

    clearError: () => {
      set({ error: undefined });
    },

    setQuerySource: (source: ExplorerSource | null) => {
      set(
        produce((draft) => {
          if (source) {
            draft.metricGroups[0].source = source;
          } else {
            delete draft.metricGroups[0].source;
          }
        }),
      );
    },

    fetchPlayerFilterOptions: async () => {
      const {
        api,
        metricGroups,
        playerFilterOptions,
        playerFilterOptionsController,
      } = get();

      // Cancel any in-flight request
      if (playerFilterOptionsController) {
        playerFilterOptionsController.abort();
      }

      // Extract current Season/League values from metricGroups[0].filters
      const queryFilters = metricGroups[0].filters;
      const seasonFilter = queryFilters.find((f) => f.name?.value === "Season");
      const leagueFilter = queryFilters.find((f) => f.name?.value === "League");

      const seasonValues = seasonFilter?.values;
      const seasons =
        seasonValues && Array.isArray(seasonValues)
          ? seasonValues.map((v) => v.value).join(",")
          : "";
      const leagueValues = leagueFilter?.values;
      const leagues =
        leagueValues && Array.isArray(leagueValues)
          ? leagueValues.map((v) => v.value).join(",")
          : "";

      // Don't fetch if no context (API returns empty list anyway)
      if (!seasons && !leagues) {
        set({
          playerFilterOptions: null,
          playerFilterOptionsController: undefined,
        });
        return;
      }

      const requestedScope = { seasons, leagues };

      // Check if we already have values for this exact scope
      if (
        playerFilterOptions &&
        playerFilterOptions.scope.seasons === requestedScope.seasons &&
        playerFilterOptions.scope.leagues === requestedScope.leagues &&
        playerFilterOptions.values.length > 0 &&
        !playerFilterOptions.loading
      ) {
        return; // Already fetched for this scope
      }

      // Create new AbortController for this request
      const controller = new AbortController();

      // Set loading state
      set({
        playerFilterOptions: {
          values: [],
          loading: true,
          scope: requestedScope,
        },
        playerFilterOptionsController: controller,
      });

      try {
        const response = await api.getPlayerFilterOptions(
          { seasons, leagues },
          { signal: controller.signal },
        );

        // Validate scope before updating - ensure response matches current state
        const currentFilters = get().metricGroups[0].filters;
        const currentSeasonFilter = currentFilters.find(
          (f) => f.name?.value === "Season",
        );
        const currentLeagueFilter = currentFilters.find(
          (f) => f.name?.value === "League",
        );
        const currentSeasonValues = currentSeasonFilter?.values;
        const currentSeasons =
          currentSeasonValues && Array.isArray(currentSeasonValues)
            ? currentSeasonValues.map((v) => v.value).join(",")
            : "";
        const currentLeagueValues = currentLeagueFilter?.values;
        const currentLeagues =
          currentLeagueValues && Array.isArray(currentLeagueValues)
            ? currentLeagueValues.map((v) => v.value).join(",")
            : "";

        // Only update if the scope still matches what we requested
        if (
          currentSeasons === requestedScope.seasons &&
          currentLeagues === requestedScope.leagues
        ) {
          set({
            playerFilterOptions: {
              values: response.data.values,
              loading: false,
              scope: requestedScope,
            },
            playerFilterOptionsController: undefined,
          });
        }
      } catch (error: any) {
        // Silently ignore aborted requests
        if (error.name === "AbortError" || controller.signal.aborted) {
          return;
        }

        set({
          playerFilterOptions: {
            values: [],
            loading: false,
            error: `Failed to load player options: ${error.message}`,
            scope: requestedScope,
          },
          playerFilterOptionsController: undefined,
        });
      }
    },
  }));

type Selector<T> = (state: ExplorerState & ExplorerActions) => T;

interface ExplorerStoreContextValue {
  useStore: <T>(selector: Selector<T>) => T;
}

const ExplorerStoreContext = createContext<ExplorerStoreContextValue | null>(
  null,
);

export function useExplorerStore<T>(selector: Selector<T>): T {
  const context = useContext(ExplorerStoreContext);
  if (!context) {
    throw new Error(
      "useExplorerStore must be used within an ExplorerStoreProvider",
    );
  }
  return context.useStore(selector);
}

interface ExplorerStoreProviderProps {
  children: React.ReactNode;
}

export function ExplorerStoreProvider({
  children,
}: ExplorerStoreProviderProps) {
  const environment = useLeagueEnvironment();
  const store = useMemo(() => createExplorerStore(environment), [environment]);
  const useStore = useCallback(
    function useStore<T>(selector: Selector<T>): T {
      return store(selector);
    },
    [store],
  );

  return (
    <ExplorerStoreContext.Provider value={{ useStore }}>
      {children}
    </ExplorerStoreContext.Provider>
  );
}
